import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { applyTheme, getThemePreference, watchSystemTheme } from "../lib/theme";
import { isPublicPath } from "../lib/publicPaths";

/**
 * Decides which theme is actually on screen, per route.
 *
 * The signed-out pages are always light. They are the brand's front door and should look like the
 * business card to everyone who arrives, rather than inheriting a preference set inside the app -- or,
 * for a first-time visitor, whatever their laptop happens to be set to.
 *
 * Crucially this never *writes* the preference. Someone who chose dark keeps it: they see a light
 * marketing page, sign in, and land back in their dark app. Forcing the attribute rather than the
 * stored value is the whole reason those are separate concepts.
 *
 * Renders nothing -- it exists for the effect. It sits inside the router so it can see the route.
 */
export function ThemeController() {
  const { pathname } = useLocation();
  const isPublic = isPublicPath(pathname);

  useEffect(() => {
    applyTheme(isPublic ? "light" : getThemePreference());
  }, [isPublic, pathname]);

  // A "match my device" preference should follow the device changing while the page is open -- but not
  // while a public page is deliberately pinned to light.
  useEffect(() => {
    if (isPublic) return;
    return watchSystemTheme();
  }, [isPublic]);

  return null;
}
